import { Label } from 'components/atoms';
import { LabeledStatus, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiOpenAiLogo } from 'react-icons/pi';
import { ARTIST_ROLE, GENRE, STATUS } from 'services/types';
import { IVideoMutationForm, TRACK_CENSORIOUS } from 'services/video/types';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import {
  conditionalField,
  credensialsValidation,
  externalIdsValidation,
  getTabError,
  requiredEnumSelect,
  titlesValidation,
} from 'utils/global/validation.utils';
import { array, number, object, string } from 'yup';
import { artistsValidation } from '../music-product-digital/MusicProductDigitalForm';
import { IFormikProps, IFormProps } from '../types';

import FormSubmitButtons from 'components/molecules/buttons/formSubmitButtons/FormSubmitButtons';
import Tabs from 'components/molecules/tabs';
import { FormObserver } from 'hooks/useFormObserver';
import { useStoreState } from 'providers/StoreStateContext';
import VideoData from './tabs/VideoData';
import VideoFile from './tabs/VideoFile';
import VideoProducts from './tabs/VideoProducts';
import VideoSubtitles from './tabs/VideoSubtitles';

export enum VideoTabs {
  data,
  file,
  artists,
  titles,
  subtitles,
  external,
  contracts,
  products,
}

export const videoInitialValues: IVideoMutationForm = {
  censorious: TRACK_CENSORIOUS.unknown,
  genre: { id: -1, name: '' },
  language: -1,
  // source: TRACK_SOURCE.unknown,
  status: STATUS.draft,
  mainTitle: {
    language: -1,
    title: '',
    version: '',
  },
  cline: { name: '', year: '' },
  pline: { name: '', year: '' },
  isrc: '',
  otherTitles: [],
  artists: [],
  isCreatedWithAi: false,
  subtitles: [],
  externalIds: [],
  canUpdateISRC: true,
  isInstrumental: true,
  version: 0,
};

export const videoValidation = object().shape({
  status: requiredEnumSelect(STATUS),
  mainTitle: titlesValidation,
  language: number()
    .not([-1])
    .required(() => i18next.t('formik-error.required')),
  censorious: conditionalField(requiredEnumSelect(TRACK_CENSORIOUS)),
  genre: conditionalField(object().shape({ id: requiredEnumSelect(GENRE) })),

  // isrc: conditionalField(string().required(() => i18next.t('formik-error.required'))),
  pline: credensialsValidation('status'),
  cline: credensialsValidation('status'),
  otherTitles: array().of(titlesValidation),
  externalIds: array().of(externalIdsValidation),
  artists: conditionalField(
    array()
      .of(artistsValidation)
      .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
      .test(
        'has-main-artist',
        () =>
          i18next.t('formik-error.what-should-have-main-artist', {
            what: i18next.t('video.what'),
          }),
        (artist) => artist && artist.some((item) => item.role === ARTIST_ROLE.mainArtist),
      )
      .test(
        'has-composer',
        () =>
          i18next.t('formik-error.what-should-have-composer', {
            what: i18next.t('video.what'),
          }),
        (artist) => artist && artist.some((item) => item.role === ARTIST_ROLE.composer),
      ),
    'status',
    [-1, 1],
    array().of(artistsValidation),
  ),
  file: conditionalField(
    string().when('originalVideoLink', {
      is: (link?: string) => link === undefined,
      then: (schema) => schema.required(() => i18next.t('formik-error.required')),
      otherwise: (schema) => schema.optional(),
    }),
  ),
  originalVideoLink: conditionalField(
    string().when('file', {
      is: (file?: File) => file === undefined,
      then: (schema) => schema.required(() => i18next.t('formik-error.required')),
      otherwise: (schema) => schema.optional(),
    }),
  ),
});

export interface IVideoForm extends FormikConfig<IVideoMutationForm>, IFormProps, II18nextPrefix {}

const VideoForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = videoInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: IVideoForm) => {
  const { t } = useTranslation();
  const { getStatusOptions } = useEnumToSelectOptions();
  const { setForm } = useStoreState();
  return (
    <Formik
      {...props}
      validationSchema={videoValidation}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}
    >
      {({ values, setFieldValue, status, errors }: IFormikProps<IVideoMutationForm>) => (
        <Form className="w-full h-full">
          <FormObserver<IVideoMutationForm>
            onChange={(newValues) => {
              setForm('videoForm', newValues);
            }}
          />
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={tittle}
              path={backPath ?? 'video'}
              topbarContent={
                <div className="flex  justify-end">
                  <div className="flex items-center gap-4 justify-end ">
                    {values.isCreatedWithAi && (
                      <Label color="blue" size="compact">
                        <PiOpenAiLogo />
                      </Label>
                    )}

                    {!!initialValues.mainTitle.title.length && status?.displayMode && (
                      <LabeledStatus status={values.status} noname />
                    )}

                    {!status?.displayMode && <FormSubmitButtons statusName="status" />}
                    {topbarContent}
                  </div>
                </div>
              }
            >
              <TabsNavigation
                tabs={[
                  {
                    value: VideoTabs.data,
                    name: 'data',
                    error: getTabError([
                      errors.mainTitle?.title,
                      errors.mainTitle?.version,
                      errors.language,
                      errors.isrc,
                      errors.censorious,
                      errors.genre?.id,
                      errors.isCreatedWithAi,
                      errors.isInstrumental,
                      errors.cline,
                      errors.pline,
                    ]),
                  },
                  {
                    value: VideoTabs.file,
                    name: 'file',
                    error: errors.file || errors.originalVideoLink,
                  },
                  {
                    value: VideoTabs.artists,
                    name: 'artists',
                    error: errors.artists,
                  },

                  {
                    value: VideoTabs.titles,
                    name: 'titles',
                    error: errors.otherTitles,
                  },
                  ...(!values.isInstrumental
                    ? [
                        {
                          value: VideoTabs.subtitles,
                          name: 'subtitles',
                          error: errors.subtitles,
                        },
                      ]
                    : []),
                  {
                    value: VideoTabs.external,
                    name: 'external',
                    error: errors.externalIds,
                  },
                  {
                    value: VideoTabs.contracts,
                    name: 'contracts',
                  },
                  {
                    value: VideoTabs.products,
                    name: 'products',
                  },
                ]}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

VideoForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'data', element: <VideoData /> },
    { path: 'file', element: <VideoFile /> },
    { path: 'subtitles', element: <VideoSubtitles /> },
    { path: 'artists', element: <Tabs.ArtistsRole /> },
    { path: 'contracts', element: <Tabs.ProductContracts /> },
    { path: 'titles', element: <Tabs.Titles parent="VIDEO" /> },
    { path: 'external', element: <Tabs.ExternalId parent="VIDEO" /> },
    { path: 'products', element: <VideoProducts /> },
  ].filter((children) => !disabledChildren?.includes(children.path));

export default VideoForm;
